import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Spin, Table } from "antd";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { StyledTableContainer } from "./styles";
import Loader from "../Loader";

const DataTable = forwardRef(
  (
    {
      headers = [],
      data = [],
      tableSize = "small",
      totalRecords = 0,
      bordered = true,
      isAPiCallLoading = false,
      paginationPosition = "bottomRight",
      onRowClick: onRowClickFn,
      variant,
      multipleRowSelection = false,
      style = {},
      pagination = null,
      setPagination,
      noDataMessage = "No Data Found",
      defaultSelectedRows = [],
      className = "",
      selectionColumnVisible = false,
      rowUniqueId = "id",
      allowTableSorting = false,
      rowHoverable = true,
      virtual = false,
      tableMaxWidth = 3000,
      tableMaxHeight = 450,
      onTableChange,
      autoResetSelection = true,
      onPageChange,
      fixedHeader = true, // custom setting not ant default
    },
    ref
  ) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [sortedInfo, setSortedInfo] = useState({});
    useEffect(() => {
      if (autoResetSelection) {
        setSelectedRowKeys([]);
        setSelectedRows([]);
        onSelectChange([], []);
      }
    }, [data, isAPiCallLoading, autoResetSelection]);

    useEffect(() => {
      // setSortedInfo(null);
    }, [data]);

    const resetAll = () => {
      setSelectedRowKeys([]);
      setSelectedRows([]);
      onSelectChange([], []);
      setSortedInfo(null);
    };

    useImperativeHandle(ref, () => ({
      resetAll,
    }));

    const DTSortIcon = ({ iconType }) => {
      switch (iconType) {
        case "ascend":
          return (
            <span className="custom-sort-icon">
              <CaretUpOutlined />
              <CaretUpOutlined />
            </span>
          );
        case "descend":
          return (
            <span className="custom-sort-icon">
              <CaretDownOutlined />
              <CaretDownOutlined />
            </span>
          );
        default:
          return (
            <span className="custom-sort-icon custom-sort-default">
              <CaretUpOutlined />
              <CaretDownOutlined />
            </span>
          );
      }
    };

    const columns = useMemo(() => {
      return headers.map(({ key, text, sorter, render, ellipsis, width }) => {
        const sortMethod = (a, b) => {
          const valueA = a[key];
          const valueB = b[key];

          // Empty, null, or undefined
          if (valueB == null || valueB === "") return 1;
          if (valueA == null || valueA === "") return -1;

          // Date

          let dateA, dateB;
          if (typeof valueA == "string") dateA = Date.parse(valueA);

          if (typeof valueB == "string") dateB = Date.parse(valueB);
          if (!isNaN(dateA) && !isNaN(dateB)) {
            return dateA - dateB;
          }
          // Numbers
          const isPureNumber = (val) => {
            return (
              typeof val === "number" ||
              (typeof val === "string" && /^\d+(\.\d+)?$/.test(val.trim()))
            );
          };

          const isNumberA = isPureNumber(valueA);
          const isNumberB = isPureNumber(valueB);
          if (isNumberA && isNumberB) {
            return parseFloat(valueA) - parseFloat(valueB);
          }

          // Fallback
          return valueA.toString().localeCompare(valueB.toString(), undefined, {
            numeric: true,
            sensitivity: "base",
          });
        };

        return {
          title: text,
          dataIndex: key,
          key,
          render: render,
          ellipsis: ellipsis,
          width: width,
          sorter:
            allowTableSorting && data.length > 1 ? sorter || sortMethod : false,
          sortOrder: sortedInfo?.columnKey === key ? sortedInfo?.order : null,
          showSorterTooltip: { target: "sorter-icon" },
          sortIcon: ({ sortOrder }) => {
            switch (sortOrder) {
              case "ascend":
                return <DTSortIcon iconType="ascend" />;
              case "descend":
                return <DTSortIcon iconType="descend" />;
              default:
                return <DTSortIcon iconType="default" />;
            }
          },
        };
      });
    }, [headers, allowTableSorting, data, sortedInfo]);

    // onSelectChange
    const onSelectChange = (currentSelectedRowKeys, currentSelectedRows) => {
      setSelectedRowKeys(currentSelectedRowKeys);
      setSelectedRows(currentSelectedRows);
      onRowClickFn?.(
        multipleRowSelection
          ? currentSelectedRows
          : currentSelectedRows[0] || null
      );
    };

    // pagination
    const paginationConfig = useMemo(() => {
      const isHaveMorePages =
        totalRecords > Math.min(...["25", "50", "100"].map(Number));
      return pagination
        ? {
            current: pagination.pageIndex + 1,
            pageSize: pagination.pageSize,
            total: totalRecords,
            onChange: (page, pSize) => {
              setPagination?.((prev) => ({ ...prev, pageIndex: page - 1 }));
              onPageChange(page - 1, pSize);
              onSelectChange([], []);
            },
            position: [paginationPosition],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            showSizeChanger: isHaveMorePages,
            pageSizeOptions: ["25", "50", "100"],
            onShowSizeChange: (current, size) => {
              setPagination?.((prev) => ({ ...prev, pageSize: size }));
            },
            itemRender: (page, type, originalElement) => {
              if (!isHaveMorePages && type) return null;
              if (!isHaveMorePages && page) return null;
              return originalElement;
            },
            showQuickJumper: true,

            locale: { items_per_page: "" },
          }
        : false;
    }, [pagination, paginationPosition, totalRecords, setPagination]);

    // table change
    const handleTableChange = useCallback(
      (pagination, filters, sorter) => {
        setSortedInfo(sorter?.order ? sorter : null);

        if (setPagination) {
          setPagination({
            pageIndex: pagination.current - 1,
            pageSize: pagination.pageSize,
          });
        }

        setSelectedRowKeys([]);
        onSelectChange([], []);
        if (onTableChange) {
          onTableChange(pagination, filters, sorter);
        }
      },
      [setPagination, onTableChange]
    );

    //  row selection
    const rowSelection = {
      selectedRowKeys,
      onChange: onSelectChange,
      selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
      getCheckboxProps: (record) => ({
        disabled: record?.disabled,
      }),
      columnWidth: 50,
    };

    const onRowHandler = (record) => ({
      onClick: (event) => {
        const targetElement = event.target;

        // Ignore clicks on buttons or links
        if (
          ["BUTTON", "A"].includes(targetElement.tagName) ||
          targetElement.closest("button") ||
          targetElement.closest("a") ||
          targetElement.hasAttribute("onClick")
        ) {
          return;
        }

        const rowKey = record[rowUniqueId];

        let updatedSelectedRowKeys;

        if (!multipleRowSelection) {
          updatedSelectedRowKeys = selectedRowKeys.includes(rowKey)
            ? selectedRowKeys
            : [rowKey];
        } else {
          updatedSelectedRowKeys = selectedRowKeys.includes(rowKey)
            ? selectedRowKeys.filter((key) => key !== rowKey)
            : [...selectedRowKeys, rowKey];
        }

        const updatedSelectedRows = data.filter((item) =>
          updatedSelectedRowKeys.includes(item[rowUniqueId])
        );

        onSelectChange(updatedSelectedRowKeys, updatedSelectedRows);
      },
      style: {
        cursor: rowHoverable ? "pointer" : "default",
      },
    });

    const getClassNames = () =>
      [
        className,
        variant === "outlined" ? "table-outlined" : "",
        multipleRowSelection && !selectionColumnVisible && !virtual
          ? "selection-element-hide"
          : "",
        !multipleRowSelection && !selectionColumnVisible && !virtual
          ? "selection-element-hide"
          : "",
        fixedHeader ? "table-has-fixed-header" : "",
      ]
        .filter(Boolean)
        .join(" ");

    return (
      <StyledTableContainer className="data-table-main">
        <Table
          className={getClassNames()}
          columns={columns}
          dataSource={data}
          size={tableSize}
          bordered={bordered}
          rowSelection={{
            type: multipleRowSelection ? "checkbox" : "radio",
            ...rowSelection,
          }}
          rowKey={rowUniqueId}
          pagination={paginationConfig}
          loading={{
            spinning: isAPiCallLoading,
            tip: "Loading",
          }}
          scroll={{
            x: virtual ? tableMaxWidth : "max-content",
            y: virtual ? tableMaxHeight : null,
          }}
          onRow={onRowHandler}
          onChange={handleTableChange}
          style={style}
          locale={{ emptyText: noDataMessage }}
          virtual={virtual}
          tableLayout="auto"
        />
      </StyledTableContainer>
    );
  }
);

export default DataTable;
